import React from 'react';
import PropTypes from 'prop-types';
import { Link } from '@resy/ui-kitchen';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import AmexImage from '../../AmexMarketingUtils/AmexImage';
import { handleMktAnalytics } from '../../AmexMarketingUtils/AmexUtils';
import { BREAKPOINTS, CTA_EVENT_NAME } from '../../AmexMarketingUtils/constants';
import Chevron from '../../../images/amexrga/chevron-left.svg';
import './ResyRewardsBonus.scss';

const ResyRewardsBonus = ({
  bonusConfig,
  eventAnalytics,
  matchMedia,
  urlPrefix,
}) => {
  const {
    title,
    copy,
    cta_text: ctaText,
    cta_url: ctaUrl,
    images,
    image_alt: imageAlt = '',
    overlay_icon: overlayIcon,
    overlay_icon_alt: overlayIconAlt = '',
    footnote,
    footnote_url: footnoteUrl,
  } = bonusConfig || {};

  if (!title) return null;

  const onCtaClick = () => {
    const extraParams = {
      action_name: 'Bonus Points CTA - Click',
      item_name: ctaText,
    };
    handleMktAnalytics(eventAnalytics, CTA_EVENT_NAME, extraParams);
  };

  const renderFootnote = () => {
    const footnoteText = (
      <div
        className="ResyRewardsBonus__footnote-text"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: sanitizeString(footnote) }}
      />
    );

    if (!footnoteUrl) {
      return <div className="ResyRewardsBonus__footnote">{footnoteText}</div>;
    }

    const onFootnoteClick = () => {
      handleMktAnalytics(eventAnalytics, CTA_EVENT_NAME, {
        action_name: 'Bonus Points Footnote - Click',
        item_name: footnoteUrl,
      });
    };

    return (
      <Link
        className="ResyRewardsBonus__footnote ResyRewardsBonus__footnote--link"
        href={footnoteUrl}
        onClick={onFootnoteClick}
      >
        {footnoteText}
        <span className="ResyRewardsBonus__footnote-icon" aria-hidden="true">
          <Chevron />
        </span>
      </Link>
    );
  };
  return (
    <section className="ResyRewardsBonus">
      <div className="container">
        <div className="ResyRewardsBonus__card">
          {images && (
            <div className="ResyRewardsBonus__image">
              <AmexImage
                images={images}
                imageAlt={imageAlt}
                mediaBreakpoint={BREAKPOINTS.MEDIUM}
                matchMedia={matchMedia}
                urlPrefix={urlPrefix}
              />
              {overlayIcon && (
                <img
                  className="ResyRewardsBonus__overlay-icon"
                  src={`${urlPrefix}${overlayIcon}`}
                  alt={overlayIconAlt}
                />
              )}
            </div>
          )}
          <div className="ResyRewardsBonus__content">
            <h2
              className="ResyRewardsBonus__title"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: sanitizeString(title) }}
            />
            <div className="ResyRewardsBonus__footer">
              {copy && (
                <p
                  className="ResyRewardsBonus__copy"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: sanitizeString(copy) }}
                />
              )}
              {ctaText && ctaUrl && (
                <Link
                  className="ResyRewardsBonus__cta"
                  href={ctaUrl}
                  onClick={onCtaClick}
                >
                  {ctaText}
                </Link>
              )}
            </div>
          </div>
        </div>
        {footnote && renderFootnote()}
      </div>
    </section>
  );
};

ResyRewardsBonus.propTypes = {
  bonusConfig: PropTypes.shape({
    title: PropTypes.string,
    copy: PropTypes.string,
    cta_text: PropTypes.string,
    cta_url: PropTypes.string,
    image_alt: PropTypes.string,
    images: PropTypes.shape({
      desktop: PropTypes.string,
      mobile: PropTypes.string,
    }),
    footnote: PropTypes.string,
    footnote_url: PropTypes.string,
    overlay_icon: PropTypes.string,
    overlay_icon_alt: PropTypes.string,
  }),
  eventAnalytics: PropTypes.func,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  urlPrefix: PropTypes.string.isRequired,
};

ResyRewardsBonus.defaultProps = {
  bonusConfig: null,
  eventAnalytics: null,
};

export default ResyRewardsBonus;
